import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { gql, useApolloClient, useReactiveVar } from "@apollo/client";
import BrandScreenInner from "./BrandScreenInner";
import ErrorScreen from "./ErrorScreen";
import LoadingScreen from "./LoadingScreen";
import Button, { ButtonVariant } from "./Button";
import TitleBar, { WithfriendsAction } from "./WithfriendsTitleBar";
import NotConnectedScreen from "./NotConnectedScreen";
import { useUpdateBrandV1Mutation, useUpdateBrandV2Mutation, useBrandScreenBusinessQuery, UpdateBrandFragmentV1FragmentDoc, UpdateBrandFragmentV2FragmentDoc, useUpdateThemeMutation, UpdateThemeFieldInputFragmentDoc } from "../types";
import { currentBusinessIdVar, startPrimaryAction, completePrimaryAction, primaryActionLoadingVar, dummyFunc } from "../helpers/client";
import { useAppBridge } from "@shopify/app-bridge-react";
import { Redirect } from "@shopify/app-bridge/actions";
// @ts-ignore
import { useNavigate } from "react-router-dom";
gql `
query BrandScreenBusiness($id: ID!) {
  business(id: $id) {
    id
    alias
    status
    shopifyVersion
    theme {
      id
    }
  }
}

mutation UpdateTheme($businessId: ID!, $theme: ThemeInput!){
  updateTheme(businessId: $businessId, themeInput: $theme) {
    theme {
      ...UpdateThemeFieldInput
    }
  }
}

mutation UpdateBrandV1($id: ID!, $business: BusinessInput!) {
  updateBusiness(id: $id, business: $business) {
    ...UpdateBrandFragmentV1
  }
}

mutation UpdateBrandV2($id: ID!, $business: BusinessInput!) {
  updateBusiness(id: $id, business: $business) {
    ...UpdateBrandFragmentV2
  }
}

fragment UpdateBrandFragmentV1 on Business {
  id
  oneLiner
  mission
  enableEmailUpsell
  enableSmsUpsell
  enablePastCustomerUpsell
  enableRedirectUpsell
  enableTips
}

fragment UpdateBrandFragmentV2 on Business {
  id
  oneLiner
  mission
  enableEmailUpsell
  enableSmsUpsell
  enablePastCustomerUpsell
  enableRedirectUpsell
  enableFAQ
  enableBadge
  enableNavigationLink
  enableAddToCartUpsell
  enableSidebarUpsell
}
`;
const BrandScreen = ({ useSyncShopifyDataMutation = dummyFunc, fromPreOnboarding = false, standalone = false }) => {
    const currentBusinessId = useReactiveVar(currentBusinessIdVar);
    const { data, loading, error } = useBrandScreenBusinessQuery({ variables: { id: currentBusinessId } });
    const primaryActionLoading = useReactiveVar(primaryActionLoadingVar);
    // TODO - can't seem to abstract this out without React crashing, so we gotta copy and paste it into any relevant file
    let app, redirect = null;
    let navigate = useNavigate();
    try {
        app = useAppBridge();
        redirect = Redirect.create(app);
    }
    catch (e) { }
    const VisitLink = (url) => {
        (redirect ?
            redirect.dispatch(Redirect.Action.APP, url) :
            navigate(url));
    };
    // TODO - end
    const client = useApolloClient();
    const [updateBrandV1] = useUpdateBrandV1Mutation();
    const [updateBrandV2] = useUpdateBrandV2Mutation();
    const [updateTheme] = useUpdateThemeMutation();
    const [syncToShopify] = useSyncShopifyDataMutation({
        variables: {
            id: currentBusinessId
        },
        skip: !currentBusinessId,
    });
    if (!currentBusinessId)
        return _jsx(NotConnectedScreen, { action: WithfriendsAction.Brand });
    if (loading)
        return _jsx(LoadingScreen, {});
    if (error)
        return _jsx(ErrorScreen, { message: error.message });
    if (!data)
        return null;
    const saveBrand = async () => {
        var _a, _b, _c, _d;
        startPrimaryAction();
        try {
            const businessCacheData = client.readFragment({
                id: `Business:${currentBusinessId}`,
                fragment: ((_a = data.business) === null || _a === void 0 ? void 0 : _a.shopifyVersion) != 2 ? UpdateBrandFragmentV1FragmentDoc : UpdateBrandFragmentV2FragmentDoc
            });
            const updateParams = {
                variables: {
                    id: currentBusinessId,
                    business: businessCacheData,
                },
            };
            if (((_b = data.business) === null || _b === void 0 ? void 0 : _b.shopifyVersion) != 1)
                await updateBrandV1(updateParams);
            else
                await updateBrandV2(updateParams);
        }
        catch (err) { }
        // Theme
        if ((_d = (_c = data === null || data === void 0 ? void 0 : data.business) === null || _c === void 0 ? void 0 : _c.theme) === null || _d === void 0 ? void 0 : _d.id) {
            const themeCacheData = client.readFragment({
                id: `Theme:${data.business.theme.id}`,
                fragment: UpdateThemeFieldInputFragmentDoc,
            });
            const themeUpdateParams = {
                variables: {
                    businessId: currentBusinessId,
                    theme: themeCacheData,
                },
            };
            await updateTheme(themeUpdateParams);
        }
        completePrimaryAction();
    };
    const standaloneButton = async () => {
        await saveBrand();
        await syncToShopify();
    };
    const previewButton = async () => {
        await saveBrand();
        await syncToShopify();
        VisitLink("/experience");
    };
    const continueButton = async () => {
        await saveBrand();
        await syncToShopify();
        if (fromPreOnboarding) {
            const button = document.querySelector(`.wf-brand-continue-${currentBusinessId}`);
            if (button) {
                button.click();
            }
            else {
                console.warn('Button not found in DOM');
            }
        }
        else {
            VisitLink("/launch");
        }
    };
    return (_jsxs("div", { className: "w-full flex flex-col justify-center", children: [_jsx(TitleBar, { primaryAction: WithfriendsAction.SaveBrand, action: WithfriendsAction.Brand, useSyncShopifyDataMutation: useSyncShopifyDataMutation }), _jsx("div", { className: "w-full flex justify-center", children: _jsxs("div", { className: "w-full max-w-4xl flex flex-wrap", children: [!standalone && (_jsxs("div", { className: "flex flex-wrap", children: [_jsx("h2", { className: "text-4xl font-light max-w-xl w-full mb-4", children: "Style Your Member Experience." }), _jsx("p", { className: "prose prose-lg max-w-full", children: "Set the visual tone for your subscription services, and design the online experience for your members." })] })), _jsx(BrandScreenInner, { businessId: currentBusinessId, fromPreOnboarding: fromPreOnboarding })] }) }), _jsx("div", { className: "w-full my-4" }), standalone ? (_jsx("div", { className: "w-full flex flex-wrap justify-center", children: _jsx("div", { className: "flex max-w-xl w-full mb-16 sm:mb-0", children: _jsx(Button, { variant: ButtonVariant.Salmon, onClick: standaloneButton, disabled: primaryActionLoading, children: "Save" }) }) })) : (_jsxs(_Fragment, { children: [!fromPreOnboarding && (_jsx("div", { className: "w-full flex flex-wrap justify-center mb-4", children: _jsx("div", { className: "flex max-w-xl w-full", children: _jsx(Button, { variant: ButtonVariant.Outlined, onClick: previewButton, disabled: primaryActionLoading, children: "Save and Preview Experience" }) }) })), _jsx("div", { className: "w-full flex flex-wrap justify-center", children: _jsx("div", { className: "flex max-w-xl w-full mb-16 sm:mb-0", children: _jsx(Button, { variant: ButtonVariant.Salmon, onClick: continueButton, disabled: primaryActionLoading, children: fromPreOnboarding ? "Save and Continue" : "Save and Continue to Launch!" }) }) })] })), _jsx("div", { className: "w-full my-4" }), _jsx("div", { className: "w-full flex-auto" })] }));
};
export default BrandScreen;
